import { cn } from "@/lib/utils";
import AnalyticsService from "@/services/analytics";
import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import ReactSmoothCollapse from "react-smooth-collapse";

interface OpeningHour {
  day: number;
  openTime: number;
  closeTime: number;
}

interface OpeningHoursProps {
  openingHours?: OpeningHour[];
  isLoading?: boolean;
  containerClassName?: string;
}

const OpeningHours: React.FC<OpeningHoursProps> = ({
  openingHours,
  isLoading,
  containerClassName,
}) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    if (expanded) {
      AnalyticsService.trackGAEventHandler("closedOpeningHours");
      AnalyticsService.trackMx("closedOpeningHours", {});
    } else {
      AnalyticsService.trackGAEventHandler("checkedOpeningHours");
      AnalyticsService.trackMx("checkedOpeningHours", {});
    }

    setExpanded(!expanded);
  };

  const getTodaysOpeningHours = (): string => {
    const today = new Date().getDay(); // Gets today's day of the week as a number
    const openingHoursToday = openingHours?.find((hour) => hour.day === today);

    if (openingHoursToday) {
      const openTime = formatTime(openingHoursToday.openTime);
      const closeTime = formatTime(openingHoursToday.closeTime);
      return `${openTime} - ${closeTime}`;
    }
    return "Closed Today";
  };

  const formatTime = (time: number): string => {
    // Assuming the time is in HHMM format
    const hours = Math.floor(time / 100);
    const minutes = time % 100;
    const amPm = hours >= 12 ? "PM" : "AM";

    // Convert hour to 12-hour format
    const formattedHour = hours % 12 || 12; // Converts '0' to '12' for 12 AM

    return `${formattedHour.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")} ${amPm}`;
  };

  const weekdays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  if (isLoading) {
    return <div className={containerClassName}>Loading...</div>;
  }

  return (
    <div onClick={toggleExpanded} className={containerClassName}>
      <div
        className="flex flex-row justify-center items-center pr-4"
        tabIndex={0}
        aria-label={`OPening Hours: ${getTodaysOpeningHours()}`}
      >
        <p aria-hidden="true">Hours: {getTodaysOpeningHours()}</p>
        <ChevronDown
          size={24}
          className={cn(
            expanded ? "transition rotate-180" : "transition",
            "ml-4"
          )}
          aria-hidden="true"
        />
      </div>
      <ReactSmoothCollapse expanded={expanded}>
        <div className="">
          {openingHours?.map((hour) => {
            const openTime = formatTime(hour.openTime);
            const closeTime = formatTime(hour.closeTime);
            const dayName = weekdays[hour.day];
            return (
              <div
                key={dayName}
                className="flex flex-row items-start justify-end"
                tabIndex={0}
                aria-label={`${dayName} ${`${openTime} - ${closeTime}`}`}
              >
                <p className="mr-4" aria-hidden="true">
                  {dayName}
                </p>
                <p aria-hidden="true">{`${openTime} - ${closeTime}`}</p>
              </div>
            );
          })}
        </div>
      </ReactSmoothCollapse>
    </div>
  );
};

export default OpeningHours;
